import { useEffect, useRef, useState } from "react"
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from "@mui/material"

/**
 * One picker, typed into rather than scrolled through.
 *
 * ── Why a plain select stopped working ──────────────────────────────────────
 *
 * A dropdown of four is a list you read. A dropdown of eighty is a list you
 * drag through, and MUI's select does not answer first-letter keystrokes the
 * way a native one does — so the mouse was the only way to reach anything near
 * the bottom. That is true of clients, and it is just as true of projects,
 * tasks and people, which outnumber them.
 *
 * ── Why one component and not four ──────────────────────────────────────────
 *
 * Client, Project, Task and Person differ in three things: what to call an
 * option, what text should match it, and what to say when nothing is chosen.
 * Everything else — open on click, filter as you type, Enter takes the
 * highlighted row, clear goes back to unfiltered — is the same behaviour, and
 * four copies of it drift. So the behaviour lives here and the vocabulary is
 * passed in.
 *
 * ── The value is an id ──────────────────────────────────────────────────────
 *
 * Callers store ids: from a form, from the server, from the URL. Taking an
 * option object would make every one of them look the row up first, which is
 * the same lookup written a dozen times. Compared as strings, because an id
 * arrives as a number in one place and a string in another — and `0` is a real
 * organization.
 *
 * ── Empty means two different things ────────────────────────────────────────
 *
 * On a filter it means everything; on a form it means the question has not been
 * answered. Both are the empty string underneath, and the only part visible to
 * the person is what the box says when nothing is in it. Hence `emptyLabel`
 * rather than a mode flag.
 */
export default function SearchSelect({
  options = [],
  value,
  onChange,
  label,
  /** What the box says when nothing is chosen. On a filter, "All projects". */
  emptyLabel = "Choose one…",
  /** The option's text. */
  getLabel = (option) => option?.label ?? "",
  /** A second line under it, where one line cannot identify the row. */
  getCaption,
  /** Everything a match should be tried against, beyond the label. */
  getSearchText,
  noOptionsText = "Nothing matches",
  /**
   * Called with what has been typed, debounced, when the full set is bigger
   * than the page the caller holds. See below for why this exists.
   */
  onSearch,
  /** Change this when the option set means something else. See below. */
  searchKey,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  size = "small",
  sx,
  error = false,
}) {
  const [query, setQuery] = useState("")
  const selected = options.find((option) => String(option.id) === String(value ?? "")) ?? null

  /*
   * ── Typing has to reach past the page that was loaded ─────────────────────
   *
   * A picker filters what it was handed. Where that is one page of a longer
   * list — the staff directory stops at 200 — filtering it locally means
   * everybody past the cut is unreachable, and the box gives no sign of it: it
   * says "Nobody matches" for a colleague who plainly exists.
   *
   * So the caller is told what was typed and can go and ask the server. Local
   * filtering stays on top of whatever comes back, which keeps the list correct
   * between the keystroke and the response instead of flashing every row.
   */
  /*
   * ── Why the callback is held in a ref ─────────────────────────────────────
   *
   * A caller writes `onSearch` as a `useCallback` over whatever narrows the
   * list — the chosen client and project, for the task box. That identity
   * changes the moment somebody picks a project, and with `onSearch` in the
   * dependency array the effect re-fires WITH THE OLD QUERY. After a
   * selection the old query is the selected row's own label, so picking a
   * project re-ran the search as `q=SYDA-1: Task Alpha` and emptied the list:
   * the label matches neither the title nor the code, so the server correctly
   * answered "nothing", and the box looked broken.
   *
   * The ref keeps the latest callback without making it a trigger.
   */
  const searchRef = useRef(onSearch)
  searchRef.current = onSearch

  /*
   * `searchKey` is what a caller changes when the list means something else —
   * a different client, a different project. It resets the typed query first,
   * because carrying a search across that boundary is the bug above in its
   * other form: text typed to find something in one project, silently applied
   * to the next.
   */
  useEffect(() => {
    setQuery("")
  }, [searchKey])

  useEffect(() => {
    if (!searchRef.current) return undefined
    const timer = setTimeout(() => searchRef.current(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query, searchKey])

  /*
   * Matched on more than the label, because a label is not how people refer to
   * things. A project is quoted by its code, a task by "ONB-14", a person by
   * the email in their signature — and a picker that refuses what is printed on
   * the thing is one people stop typing into.
   */
  const filter = createFilterOptions({
    stringify: (option) => `${getLabel(option)} ${getSearchText ? getSearchText(option) : ""}`,
  })

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(event, option) => {
        /* Cleared on selection: MUI puts the chosen row's label in the input,
           and leaving that as the live query means the next refetch asks the
           server for the thing already chosen and gets nothing back. */
        setQuery("")
        onChange(option ? String(option.id) : "")
      }}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, chosen) => String(option.id) === String(chosen?.id)}
      filterOptions={filter}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      /* A floor, so a caller that sets no width still gets a box its content
         fits in. Without one the field shrinks to fit and a name wraps onto two
         lines inside a 100px box. */
      sx={{ minWidth: 200, ...sx }}
      /* Clicking opens the list, because that is what it did as a select and
         people click before they think to type. */
      onInputChange={(event, text, reason) => {
        // `reset` fires when a value is chosen, with the option's own label —
        // treating that as a search would refetch on every selection.
        if (reason !== "reset") setQuery(text)
      }}
      openOnFocus
      /* Enter takes the highlighted row. Without it, typing a whole name and
         pressing Enter leaves the box empty, which is the most annoying way a
         combo box can behave. */
      autoHighlight
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        const { key, ...rest } = props
        const caption = getCaption ? getCaption(option) : null
        return (
          <Box component="li" key={key} {...rest} sx={{ display: "block !important", py: 0.75 }}>
            <Typography variant="body2" noWrap>
              {getLabel(option)}
            </Typography>
            {/* The second line is what tells two rows with the same name apart
                — two projects called "Onboarding" under different clients, two
                people who share a first name. */}
            {caption ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {caption}
              </Typography>
            ) : null}
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          /* Always, whatever the Autocomplete's own `fullWidth`: the field
             fills the control and the control is what the caller sizes. Left
             off, MUI sizes the field to its content, and the box somebody asked
             to be 220px wide holds one that is 100. */
          fullWidth
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={emptyLabel}
        />
      )}
    />
  )
}
